import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { TemplateGanttPreview } from '../components/Configuration/TemplateGanttPreview';
import {
  BuilderStatusScreen,
  type BuilderStatus
} from '../components/Configuration/templateBuilder/BuilderStatusScreen';
import { BuilderTopBar } from '../components/Configuration/templateBuilder/BuilderTopBar';
import { resolveSaveStatus } from '../components/Configuration/templateBuilder/saveStatus';
import { TemplateTitleField } from '../components/Configuration/templateBuilder/TemplateTitleField';
import { TemplateStatsRow } from '../components/Configuration/templateBuilder/TemplateStatsRow';
import { countInterventions, sumReimbursement } from '../components/Configuration/templateStats';
import { TemplateColorPicker } from '../components/Configuration/templateBuilder/TemplateColorPicker';
import {
  TemplateLocationDropdown,
  type TemplateLocationSelection
} from '../components/Configuration/templateBuilder/TemplateLocationDropdown';
import { WeekEditorSection } from '../components/Configuration/templateBuilder/WeekEditorSection';
import type { TemplateColorPalette } from '../constants/templateColors';
import ROUTES from '../routes/routeConstants';
import {
  blankDraft,
  blankDraftWeek,
  draftFromResponse,
  draftToCreateRequest,
  draftToUpdateRequest,
  type DraftWeek,
  type TreatmentPlanTemplateDraft
} from '../components/Configuration/templateDraftTypes';
import { useTreatmentPlanTemplates } from '../hooks/useTreatmentPlanTemplates';
import { useInterventionTemplates } from '../hooks/useInterventionTemplates';
import { useLocations } from '../hooks/useLocations';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectTreatmentPlanTemplateById } from '../store/treatmentPlanTemplates/treatmentPlanTemplatesSlice';
import { showError } from '../store/errorDialog/errorDialogSlice';
import { showToast } from '../store/toast/toastSlice';
import { extractFieldErrors } from '../utils/fieldErrorUtils';
import { getApiErrorMessage } from '../utils/errorMessageUtils';
import { TEMPLATE_FIELD_PATH } from '../constants/templateFieldPaths';
import styles from './TreatmentPlanBuilderPage.module.css';

const NEW_TEMPLATE_PARAM = 'new';
const LOADING_STATUS: BuilderStatus = {
  messageKey: 'config-builder-loading',
  hasBackButton: false
};

type BuilderStatusInput = {
  hasLoadError: boolean;
  hasDraft: boolean;
  isNew: boolean;
  hasServerTemplate: boolean;
  isTemplatesLoading: boolean;
  isCatalogLoading: boolean;
  isLocationsLoading: boolean;
};

const resolveBuilderStatus = (input: BuilderStatusInput): BuilderStatus | null => {
  if (input.hasLoadError) {
    return { messageKey: 'config-builder-load-error', hasBackButton: true };
  }
  const isLoading = input.isTemplatesLoading || input.isCatalogLoading || input.isLocationsLoading;
  if (!input.hasDraft && isLoading) return LOADING_STATUS;
  if (!input.isNew && !input.hasServerTemplate && !input.isTemplatesLoading) {
    return { messageKey: 'config-builder-not-found', hasBackButton: true };
  }
  if (!input.hasDraft) return LOADING_STATUS;
  return null;
};

export const TreatmentPlanBuilderPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { templateId } = useParams<{ templateId: string }>();

  const isNew = templateId === NEW_TEMPLATE_PARAM;

  const { isLoading: templatesLoading, loadError, createTemplate, updateTemplate } =
    useTreatmentPlanTemplates();
  const serverTemplate = useAppSelector(selectTreatmentPlanTemplateById(isNew ? undefined : templateId));
  const { templates: catalog, isLoading: catalogLoading } = useInterventionTemplates();
  const { locations, isLoading: locationsLoading } = useLocations();

  const [draft, setDraft] = useState<TreatmentPlanTemplateDraft | null>(() =>
    isNew ? blankDraft() : null
  );
  const [isDirty, setIsDirty] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const titleRef = useRef<HTMLInputElement>(null);
  const hasHydratedRef = useRef(isNew);
  const hasFocusedTitleRef = useRef(false);
  // Counts every draft edit so handleSave can tell whether the user kept typing
  // while the request was in flight (those edits are not in the saved payload).
  const editCountRef = useRef(0);

  useEffect(() => {
    if (hasHydratedRef.current) return;
    if (serverTemplate) {
      hasHydratedRef.current = true;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot async hydration from Redux cache
      setDraft(draftFromResponse(serverTemplate));
    }
  }, [serverTemplate]);

  useEffect(() => {
    if (hasFocusedTitleRef.current) return;
    if (isNew && draft && titleRef.current) {
      hasFocusedTitleRef.current = true;
      titleRef.current.focus();
      titleRef.current.select();
    }
  }, [isNew, draft]);

  const updateDraft = (updater: (prev: TreatmentPlanTemplateDraft) => TreatmentPlanTemplateDraft) => {
    setDraft((prev) => (prev ? updater(prev) : prev));
    setIsDirty(true);
    editCountRef.current += 1;
  };

  const clearFieldError = (path: string) => {
    setFieldErrors((prev) => {
      if (!(path in prev)) return prev;
      const next = { ...prev };
      delete next[path];
      return next;
    });
  };

  const handleTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
    clearFieldError(TEMPLATE_FIELD_PATH.name);
    updateDraft((prev) => ({ ...prev, name: event.target.value }));
  };

  const handleColorPaletteChange = (colors: TemplateColorPalette) => {
    updateDraft((prev) => ({ ...prev, ...colors }));
  };

  const handleLocationChange = (selection: TemplateLocationSelection) => {
    updateDraft((prev) => ({
      ...prev,
      global: selection.isGlobal,
      locationIds: selection.locationIds
    }));
  };

  const handleWeeksChange = (weeks: DraftWeek[]) => {
    updateDraft((prev) => ({ ...prev, weeks }));
  };

  const handleAddWeek = () => {
    updateDraft((prev) => ({ ...prev, weeks: [...prev.weeks, blankDraftWeek()] }));
  };

  const handleSave = async () => {
    if (!draft) return;
    setFieldErrors({});
    const editCountAtSave = editCountRef.current;
    let createdId: string | undefined;
    try {
      if (draft.id) {
        await updateTemplate(draft.id, draftToUpdateRequest(draft));
      } else {
        createdId = (await createTemplate(draftToCreateRequest(draft))).id;
      }
    } catch (error) {
      const fieldErrorMap = extractFieldErrors(error);
      if (Object.keys(fieldErrorMap).length > 0) {
        setFieldErrors(fieldErrorMap);
      } else {
        console.error('Failed to save treatment plan template:', error);
        dispatch(showError({ message: getApiErrorMessage(error, t('config-builder-save-error')) }));
      }
      return;
    }
    if (!draft.id) {
      // First save of a new template: adopt the server-assigned id and swap the
      // URL from `.../new` to `.../<id>` so the page stays open, a refresh reloads
      // the right template, and the next save is an update rather than a duplicate.
      if (!createdId) {
        // The generated types promise an id, but if a backend regression omits it,
        // adopting it anyway would make the next save create a duplicate template.
        console.error('Treatment plan template was created but the response has no id');
        dispatch(showError({ message: t('config-builder-save-error') }));
        return;
      }
      const newTemplateId = createdId;
      setDraft((prev) => (prev ? { ...prev, id: newTemplateId } : prev));
      navigate(`/${generatePath(ROUTES.treatmentPlanBuilder, { templateId: newTemplateId })}`, {
        replace: true
      });
    }
    // Edits made while the request was in flight were not part of the payload —
    // keep the draft dirty so they aren't announced as saved.
    if (editCountRef.current === editCountAtSave) {
      setIsDirty(false);
    }
    dispatch(showToast({ message: t('config-builder-saved') }));
  };

  const handleBack = () => {
    if (isDirty) {
      const confirmed = window.confirm(t('config-builder-unsaved-warning'));
      if (!confirmed) return;
    }
    navigate(`/${ROUTES.configuration}`);
  };

  const builderStatus = resolveBuilderStatus({
    hasLoadError: Boolean(loadError),
    hasDraft: Boolean(draft),
    isNew,
    hasServerTemplate: Boolean(serverTemplate),
    isTemplatesLoading: templatesLoading,
    isCatalogLoading: catalogLoading,
    isLocationsLoading: locationsLoading
  });

  if (builderStatus !== null || draft === null) {
    return <BuilderStatusScreen status={builderStatus ?? LOADING_STATUS} onBack={handleBack} />;
  }

  const templateGroups = draft.weeks.flatMap((week) => week.groups.map((draftGroup) => draftGroup.group));

  return (
    <main className={styles.page}>
      <BuilderTopBar
        templateName={draft.name}
        saveStatus={resolveSaveStatus({ isDirty, isPersisted: Boolean(draft.id) })}
        onBack={handleBack}
        onSave={handleSave}
      />

      <div className={styles.titleRow}>
        <TemplateTitleField
          name={draft.name}
          strokeColor={draft.strokeColor}
          error={fieldErrors[TEMPLATE_FIELD_PATH.name]}
          isDirty={isDirty}
          inputRef={titleRef}
          onChange={handleTitleChange}
        />
        <TemplateStatsRow
          weekCount={draft.weeks.length}
          interventionCount={countInterventions(templateGroups)}
          reimbursement={sumReimbursement(templateGroups)}
          variant="header"
        />
        <div className={styles.swatches}>
          <TemplateColorPicker
            strokeColor={draft.strokeColor}
            onSelect={handleColorPaletteChange}
          />
          <TemplateLocationDropdown
            isGlobal={draft.global}
            selectedLocationIds={draft.locationIds}
            locations={locations}
            onChange={handleLocationChange}
          />
        </div>
      </div>

      <section className={styles.ganttSection}>
        <h2 className={styles.sectionLabel}>{t('config-builder-preview-label')}</h2>
        <TemplateGanttPreview template={draft} />
      </section>

      <WeekEditorSection
        weeks={draft.weeks}
        catalog={catalog}
        strokeColor={draft.strokeColor}
        fillColor={draft.fillColor}
        fieldErrors={fieldErrors}
        clearFieldError={clearFieldError}
        onChange={handleWeeksChange}
        onAddWeek={handleAddWeek}
      />
    </main>
  );
};
